import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import styles from './auth/AuthPages.module.css';
import authStyles from '../features/auth/AuthGate.module.css';
import { brandConfig } from '../config/brands';
import { useAuthBrandingSync, useAuthLogo, useAuthScheme } from '../hooks/useAuthScheme';
import { appPath } from '../utils/navigation';
import { declineInvitationByToken, previewDeclineInvitation } from '../features/invitations/api';

/**
 * 招待の辞退確認ページ (メール導線の着地先)。
 *
 * ⚠️ このページを開いただけでは辞退は確定しない。メールスキャナやプリフェッチが
 * リンクを自動的に踏むため、GET で状態を変えると本人の意図と無関係に辞退が
 * 確定してしまう。ユーザーの明示的なクリックで初めて POST する。
 * 設計: docs/requirements/invitation-block-escape-route-plan.md §7-1
 */
export function DeclineInvitationPage() {
  useAuthBrandingSync();
  const isLight = useAuthScheme() === 'light';
  const logo = useAuthLogo();
  const wrapperClass = `${styles.wrapper}${isLight ? ` ${styles.light} ${authStyles.light}` : ''}`;
  const { token } = useParams<{ token: string }>();

  const [status, setStatus] = useState<
    'verifying' | 'confirm' | 'submitting' | 'declined' | 'invalid' | 'expired' | 'accepted'
  >('verifying');
  const [organizationName, setOrganizationName] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    // 表示用の取得のみ。状態は変更しない。
    // ⚠️ /verify は使わないこと。verify は期限切れ招待を再送・復活させる副作用を持つため、
    //    メールスキャナのプリフェッチで招待が蘇ってしまう (§7-1)。
    (async () => {
      try {
        const { ok, data } = await previewDeclineInvitation(token);
        if (!ok) {
          setStatus('invalid');
          return;
        }
        setOrganizationName(data.organization_name || '');
        if (data.status === 'accepted') {
          setStatus('accepted');
        } else if (data.status === 'declined' || data.status === 'cancelled') {
          // 既に目的を達しているので完了として見せる
          setStatus('declined');
        } else if (data.is_expired) {
          // 期限切れ招待は組織作成を塞がないため辞退の必要が無い
          setStatus('expired');
        } else {
          setStatus('confirm');
        }
      } catch {
        setStatus('invalid');
      }
    })();
  }, [token]);

  const handleDecline = async () => {
    setStatus('submitting');
    setError('');
    try {
      const { ok, status: httpStatus, data } = await declineInvitationByToken(token!);
      if (ok && data.success) {
        setStatus('declined');
      } else if (httpStatus === 410) {
        setStatus('expired');
      } else if (data.code === 'INVITATION_ALREADY_ACCEPTED') {
        setStatus('accepted');
      } else {
        setError(data.error || '辞退に失敗しました');
        setStatus('confirm');
      }
    } catch {
      setError('辞退に失敗しました。時間をおいて再度お試しください。');
      setStatus('confirm');
    }
  };

  const shell = (title: string, body: ReactNode) => (
    <div className={wrapperClass}>
      <div className={styles.container}>
        <div className={styles.logoContainer}>
          <img src={logo.src} alt={brandConfig.serviceName} className={styles.logo} />
        </div>
        <div className={styles.card}>
          <h1 className={styles.title}>{title}</h1>
          {body}
        </div>
      </div>
    </div>
  );

  const homeLink = (
    <a href={appPath('/')} className={styles.backButton}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 18l-6-6 6-6" />
      </svg>
      ホームに戻る
    </a>
  );

  if (status === 'verifying') {
    return shell('招待を確認中...', (
      <div className={authStyles.loadingSpinner} style={{ margin: '24px auto' }}></div>
    ));
  }

  if (status === 'invalid') {
    return shell('無効な招待リンク', (
      <>
        <p className={styles.subtitle}>この招待リンクは無効です。</p>
        {homeLink}
      </>
    ));
  }

  if (status === 'expired') {
    return shell('招待の有効期限切れ', (
      <>
        <p className={styles.subtitle}>
          この招待リンクは有効期限が切れています。期限切れの招待は組織の作成を妨げないため、
          辞退の操作は不要です。
        </p>
        {homeLink}
      </>
    ));
  }

  if (status === 'accepted') {
    return shell('この招待は受諾済みです', (
      <>
        <p className={styles.subtitle}>
          この招待は既に受諾されています。組織から抜ける場合は組織の管理者にご連絡ください。
        </p>
        {homeLink}
      </>
    ));
  }

  if (status === 'declined') {
    return shell('招待を辞退しました', (
      <>
        <p className={styles.subtitle}>
          {organizationName ? <><strong>{organizationName}</strong> への招待を辞退しました。</> : '招待を辞退しました。'}
          <br />
          自分の組織を作成できるようになりました。
        </p>
        <a href={appPath('/organizations/create')} className={styles.ctaLink}>
          組織を作成する
        </a>
      </>
    ));
  }

  // status === 'confirm' | 'submitting'
  return shell('招待を辞退しますか？', (
    <>
      <p className={styles.subtitle}>
        {organizationName ? <><strong>{organizationName}</strong> からの招待を辞退します。</> : 'この招待を辞退します。'}
        <br />
        辞退すると、この組織は30日間あなたを再招待できなくなります。
      </p>
      {error && <div className={styles.error}>{error}</div>}
      <button
        type="button"
        onClick={handleDecline}
        disabled={status === 'submitting'}
        className={styles.submitButton}
      >
        {status === 'submitting' ? '処理中...' : '招待を辞退する'}
      </button>
      {homeLink}
    </>
  ));
}

export default DeclineInvitationPage;
